import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import {Calendar} from 'react-native-calendars';
import Svg, {Circle, G, Line, Text as SvgText} from 'react-native-svg';
import CalendarArrowLeft from '../../../../shared/assets/icons/CalendarArrowLeft';
import CalendarArrowRight from '../../../../shared/assets/icons/CalendarArrowRight';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {createCalendarTheme, createDatePickerStyles} from './DatePicker.styles';
import {DatePickerProps} from './DatePickerProps';
import {CALENDAR_HEIGHT} from '../../../../shared/constants/global';
import Typography from '../../../../shared/constants/Typography';
import Colors from '../../../../shared/theme/colors';

import {
  CLOCK_SIZE,
  CLOCK_CX,
  CLOCK_CY,
  CLOCK_BG_R,
  NUM_R,
  HAND_LEN,
  SEL_DOT_R,
  CENTER_R,
  TICK_R,
  TICK_DOT_R,
  TICK_MAJOR_R,
  HOUR_LABELS,
  MINUTE_LABELS,
  TIME_CARD_HEIGHT,
  type PickerStep,
} from './DatePicker.constants';

import {
  clockPos,
  resolveClockTouch,
  formatSelectedDate,
  pad2,
  buildDateValue,
  parseHourInput,
  parseMinuteInput,
} from './DatePicker.controller';
import {palette} from '@shared/theme/colorGroups/palette.colors';

// ─────────────────────────────────────────────────────────────────────────────
// AnalogClock
// ─────────────────────────────────────────────────────────────────────────────
interface ClockProps {
  selectedHour: number;
  selectedMinute: number;
  isHourMode: boolean;
  onHourSelect: (h: number) => void;
  onMinuteSelect: (m: number) => void;
  faceColor: string;
  handColor: string;
  numColor: string;
  containerStyle: any;
}

const AnalogClock: React.FC<ClockProps> = ({
  selectedHour,
  selectedMinute,
  isHourMode,
  onHourSelect,
  onMinuteSelect,
  faceColor,
  handColor,
  numColor,
  containerStyle,
}) => {
  const handIndex = isHourMode
    ? selectedHour === 12
      ? 0
      : selectedHour
    : Math.round(selectedMinute / 5) % 12;
  const hand = clockPos(handIndex, 12, HAND_LEN);

  // Used on web to convert absolute pageX/pageY into element-relative coords
  const containerRef = useRef<any>(null);
  const pageOffset = useRef({x: 0, y: 0});

  const measureOffset = useCallback(() => {
    if (Platform.OS === 'web') {
      containerRef.current?.measure(
        (
          _fx: number,
          _fy: number,
          _w: number,
          _h: number,
          px: number,
          py: number,
        ) => {
          pageOffset.current = {x: px, y: py};
        },
      );
    }
  }, []);

  const handleTouch = (e: any) => {
    const nE = e?.nativeEvent;
    // Guard: keyboard events (keyup/keydown) do not carry pointer coordinates.
    // nativeEvent is undefined or lacks both locationX and pageX — bail out.
    if (!nE || (nE.locationX == null && nE.pageX == null)) {
      return;
    }
    let lx: number = nE.locationX;
    let ly: number = nE.locationY;
    // Web: locationX/locationY may be undefined or relative to an SVG child.
    // Fall back to pageX/pageY minus the container's measured page offset.
    if (lx == null || isNaN(Number(lx))) {
      lx = (nE.pageX ?? 0) - pageOffset.current.x;
      ly = (nE.pageY ?? 0) - pageOffset.current.y;
    }
    const value = resolveClockTouch(lx, ly, isHourMode);
    if (value === null) return;
    if (isHourMode) {
      onHourSelect(value);
    } else {
      onMinuteSelect(value);
    }
  };

  const labels = isHourMode ? HOUR_LABELS : MINUTE_LABELS;

  // Pre-compute 60 minute tick positions
  const minuteTicks = Array.from({length: 60}, (_, i) => {
    const angle = (i / 60) * 2 * Math.PI - Math.PI / 2;
    const r = i % 5 === 0 ? TICK_MAJOR_R : TICK_DOT_R;
    return {
      cx: CLOCK_CX + TICK_R * Math.cos(angle),
      cy: CLOCK_CY + TICK_R * Math.sin(angle),
      r,
    };
  });

  return (
    <Pressable
      ref={containerRef}
      style={containerStyle}
      onLayout={measureOffset}
      onPress={handleTouch}>
      <Svg width={CLOCK_SIZE} height={CLOCK_SIZE}>
        <Circle cx={CLOCK_CX} cy={CLOCK_CY} r={CLOCK_BG_R} fill={faceColor} />
        {/* ── Minute tick dots (visible only in minute-selection mode) ── */}
        {!isHourMode &&
          minuteTicks.map((tick, i) => (
            <Circle
              key={`tick-${i}`}
              cx={tick.cx}
              cy={tick.cy}
              r={tick.r}
              fill={numColor}
              opacity={i % 5 === 0 ? 0.5 : 0.25}
            />
          ))}
        <Line
          x1={CLOCK_CX}
          y1={CLOCK_CY}
          x2={hand.x}
          y2={hand.y}
          stroke={handColor}
          strokeWidth={Typography.borderWidth.borderWidth2}
        />
        <Circle cx={CLOCK_CX} cy={CLOCK_CY} r={CENTER_R} fill={handColor} />
        {labels.map((label, i) => {
          const pos = clockPos(i, 12, NUM_R);
          const numVal =
            typeof label === 'number' ? label : parseInt(String(label), 10);
          const isSel = isHourMode
            ? numVal === selectedHour
            : numVal === (Math.round(selectedMinute / 5) * 5) % 60;
          return (
            <G key={i}>
              {isSel && (
                <Circle cx={pos.x} cy={pos.y} r={SEL_DOT_R} fill={handColor} />
              )}
              <SvgText
                x={pos.x}
                y={pos.y + 5}
                textAnchor="middle"
                fontSize={Typography.fontSize.fontSize13}
                fontWeight={isSel ? 'bold' : 'normal'}
                fill={isSel ? Colors.white : numColor}>
                {String(label)}
              </SvgText>
            </G>
          );
        })}
      </Svg>
    </Pressable>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DatePicker
//  Step 1: Calendar → tap day → auto-advance to Step 2 (if withTime)
//  Step 2: Analog clock → tap hour → minute mode; tap minute → auto-close + emit
// ─────────────────────────────────────────────────────────────────────────────
export const DatePicker = ({
  inputRef,
  showCalender,
  setDateValue,
  setShowCalnder,
  withTime = false,
  calendarOnly = false,
  initialValue,
}: DatePickerProps) => {
  const [step, setStep] = useState<PickerStep>('date');
  const [selected, setSelected] = useState('');
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(0);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>('PM');
  const [isHourMode, setIsHourMode] = useState(true);
  // Local text states for editable inputs (allow free typing)
  const [hourText, setHourText] = useState('02');
  const [minuteText, setMinuteText] = useState('00');
  // Refs to suppress useEffect sync while user is actively typing
  const editingHour = useRef(false);
  const editingMinute = useRef(false);

  // Month/year picker overlay
  const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const [currentMonth, setCurrentMonth] = useState(todayStr);
  const [pickerMode, setPickerMode] = useState<null | 'month' | 'year'>(null);
  const [calendarHeaderHeight, setCalendarHeaderHeight] = useState(0);

  const theme = useScreenTheme('DashboardScreen');
  const [calendarPos, setCalendarPos] = useState({
    top: 0,
    left: 0,
    width: 320,
    maxHeight: 630,
  });
  /**
   * Web only — true once measureInWindow has returned valid coordinates.
   * Keeps the calendar container invisible (opacity 0) on first render so it
   * never flashes at position (0, 0) before the measurement callback fires.
   * Always true on native so there is zero behaviour change on mobile.
   */
  const [isPositioned, setIsPositioned] = useState(Platform.OS !== 'web');
  const {height: screenHeight} = useWindowDimensions();
  const styles = useMemo(
    () => createDatePickerStyles(theme, calendarPos, screenHeight),
    [theme, calendarPos, screenHeight],
  );

  // Web only: reset positioning flag on close so the next open starts hidden
  // until the fresh measureInWindow completes.
  useEffect(() => {
    if (Platform.OS === 'web' && !showCalender) {
      setIsPositioned(false);
    }
  }, [showCalender]);

  // Reset / pre-populate state when picker opens
  useEffect(() => {
    if (showCalender) {
      setStep('date');
      setIsHourMode(true);
      setPickerMode(null);

      // Try to parse an existing value so the picker opens pre-populated.
      // Accepts both "MM/DD/YYYY , hh:mm AM/PM" and "MM/DD/YYYY hh:mm AM/PM".
      const match = initialValue?.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s*,?\s*(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
      );
      if (match) {
        const mon = match[1]!.padStart(2, '0');
        const day = match[2]!.padStart(2, '0');
        const year = match[3]!;
        const h = parseInt(match[4]!, 10);
        const m = parseInt(match[5]!, 10);
        const ap = match[6]!.toUpperCase() as 'AM' | 'PM';
        setSelected(`${year}-${mon}-${day}`);
        setCurrentMonth(`${year}-${mon}-01`);
        setHour(h);
        setMinute(m);
        setAmpm(ap);
        setHourText(String(h).padStart(2, '0'));
        setMinuteText(String(m).padStart(2, '0'));
      } else if (initialValue && !initialValue.includes(' ')) {
        // Date-only format: "MM/DD/YYYY"
        const parts = initialValue.split('/');
        if (parts.length === 3) {
          const [mon2, day2, year2] = parts;
          setSelected(
            `${year2}-${(mon2 ?? '').padStart(2, '0')}-${(day2 ?? '').padStart(
              2,
              '0',
            )}`,
          );
          setCurrentMonth(`${year2}-${(mon2 ?? '').padStart(2, '0')}-01`);
          const now = new Date();
          const h12 = now.getHours() % 12 || 12;
          setHour(h12);
          setMinute(now.getMinutes());
          setAmpm(now.getHours() < 12 ? 'AM' : 'PM');
        }
      } else {
        // No valid initial value — default to today's month, no selected date
        const now = new Date();
        setSelected('');
        setCurrentMonth(new Date().toISOString().slice(0, 10));
        const h12 = now.getHours() % 12 || 12;
        setHour(h12);
        setMinute(now.getMinutes());
        setAmpm(now.getHours() < 12 ? 'AM' : 'PM');
        setHourText(String(h12).padStart(2, '0'));
        setMinuteText(String(now.getMinutes()).padStart(2, '0'));
      }
    }
  }, [showCalender]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure input position and compute picker placement
  const openCalendar = useCallback(() => {
    inputRef.current?.measureInWindow(
      (x: number, y: number, w: number, h: number) => {
        const cardHeight = step === 'date' ? CALENDAR_HEIGHT : TIME_CARD_HEIGHT;
        const gap = Typography.gap.gap6;
        const spaceBelow = screenHeight - (y + h) - gap;
        const spaceAbove = y - gap;

        let top: number;
        let maxHeight: number;

        if (spaceBelow >= cardHeight) {
          // ── Fits below → open below, no scroll needed ───────────────────────
          top = y + h + gap;
          maxHeight = cardHeight;
        } else if (spaceAbove >= cardHeight) {
          // ── Fits above → open above, no scroll needed ───────────────────────
          top = y - cardHeight - gap;
          maxHeight = cardHeight;
        } else if (spaceAbove > spaceBelow) {
          // ── More room above but doesn't fully fit → pin near top, scroll ───
          top = gap;
          maxHeight = spaceAbove; // stops just above the input field
        } else {
          // ── More room below (or equal) → open below, scroll ─────────────────
          top = y + h + gap;
          maxHeight = spaceBelow;
        }

        setCalendarPos({
          top,
          left: x,
          width: w,
          maxHeight: Math.max(200, maxHeight),
        });
        // Web only: reveal calendar now that it has correct coordinates
        if (Platform.OS === 'web') {
          setIsPositioned(true);
        }
      },
    );
  }, [inputRef, screenHeight, step]);

  useEffect(() => {
    if (showCalender) openCalendar();
  }, [showCalender, openCalendar]);
  // Re-position when step changes (time card differs in height from calendar card)
  useEffect(() => {
    if (showCalender) openCalendar();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOrientationChange = () => {
    if (showCalender) openCalendar();
  };

  // Step 1 → Step 2 (or emit date-only if withTime=false or calendarOnly=true)
  const handleDayPress = (day: {dateString: string}) => {
    setSelected(day.dateString);
    if (!withTime || calendarOnly) {
      setDateValue(buildDateValue(day.dateString, 0, 0, 'AM', false));
      setShowCalnder(false);
    } else {
      setStep('time');
    }
  };

  // Keep text inputs in sync when clock selects a value (suppressed while typing)
  useEffect(() => {
    if (!editingHour.current) setHourText(pad2(hour));
  }, [hour]);
  useEffect(() => {
    if (!editingMinute.current) setMinuteText(pad2(minute));
  }, [minute]);

  // Clock: tap hour → switch to minute mode automatically
  const handleHourSelect = (h: number) => {
    setHour(h);
    setIsHourMode(false);
  };

  // Emit current state and close (used by backdrop press AND clock minute select)
  const emitAndClose = (
    overrideMinute?: number,
    overrideAmpm?: 'AM' | 'PM',
  ) => {
    if (selected) {
      const finalHour = parseHourInput(hourText, hour);
      const finalMin =
        overrideMinute !== undefined
          ? overrideMinute
          : parseMinuteInput(minuteText, minute);
      const finalAmpm = overrideAmpm !== undefined ? overrideAmpm : ampm;
      setDateValue(
        buildDateValue(selected, finalHour, finalMin, finalAmpm, true),
      );
    }
    setShowCalnder(false);
  };

  // Clock: tap minute → emit + close
  const handleMinuteSelect = (m: number) => {
    setMinute(m);
    emitAndClose(m);
  };

  // Commit hour text on blur
  const commitHour = () => {
    editingHour.current = false;
    const valid = parseHourInput(hourText, hour);
    setHour(valid);
    setHourText(pad2(valid));
  };

  // Commit minute text on blur
  const commitMinute = () => {
    editingMinute.current = false;
    const valid = parseMinuteInput(minuteText, minute);
    setMinute(valid);
    setMinuteText(pad2(valid));
  };

  const {year: selYear, label: selLabel} = formatSelectedDate(selected);

  const calendarTheme = useMemo(() => createCalendarTheme(theme), [theme]);

  // ── Month / Year picker helpers ──────────────────────────────────────────
  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentMonthDate = new Date(currentMonth + 'T00:00:00');
  const displayedYear = currentMonthDate.getFullYear();
  const displayedMonth = currentMonthDate.getMonth(); // 0-based

  // Year range: -50 to +20 from today
  const baseYear = new Date().getFullYear();
  const yearList = Array.from({length: 71}, (_, i) => baseYear - 50 + i);

  const handleMonthSelect = (monthIndex: number) => {
    const y = displayedYear;
    const m = String(monthIndex + 1).padStart(2, '0');
    setCurrentMonth(`${y}-${m}-01`);
    setPickerMode(null);
  };

  const handleYearSelect = (year: number) => {
    const m = String(displayedMonth + 1).padStart(2, '0');
    setCurrentMonth(`${year}-${m}-01`);
    setPickerMode(null);
  };

  const renderCalendarHeader = (date: any) => {
    // react-native-calendars passes XDate object; extract month/year safely
    const headerDate = date ? new Date(date.toString()) : currentMonthDate;
    const hMonth = MONTH_NAMES[headerDate.getMonth()] ?? '';
    const hYear = headerDate.getFullYear();
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: Typography.padding.padding8,
          gap: Typography.gap.gap16,
        }}>
        <TouchableOpacity
          onPress={() => setPickerMode(pickerMode === 'month' ? null : 'month')}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: Typography.gap.gap4,
          }}>
          <Text
            style={{
              color: theme.calenderTextColor ?? '#333',
              fontSize: Typography.fontSize.fontSize16,
              fontWeight: Typography.fontWeight.semiBold,
              fontFamily: Typography.fontFamily.inter,
            }}>
            {hMonth}
          </Text>
          <Text
            style={{
              color: theme.calenderTextColor ?? '#333',
              fontSize: Typography.fontSize.fontSize12,
              opacity: 0.7,
            }}>
            {pickerMode === 'month' ? '▲' : '▼'}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setPickerMode(pickerMode === 'year' ? null : 'year')}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: Typography.gap.gap4,
          }}>
          <Text
            style={{
              color: theme.calenderTextColor ?? '#333',
              fontSize: Typography.fontSize.fontSize16,
              fontWeight: Typography.fontWeight.semiBold,
              fontFamily: Typography.fontFamily.inter,
            }}>
            {hYear}
          </Text>
          <Text
            style={{
              color: theme.calenderTextColor ?? '#333',
              fontSize: Typography.fontSize.fontSize12,
              opacity: 0.7,
            }}>
            {pickerMode === 'year' ? '▲' : '▼'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <Modal
      visible={showCalender}
      transparent
      animationType="none"
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}
      onOrientationChange={handleOrientationChange}>
      <Pressable
        style={styles.backdrop}
        onPress={() =>
          step === 'time' ? emitAndClose() : setShowCalnder(false)
        }>
        <View
          style={[
            styles.calendarContainer,
            !isPositioned && {opacity: 0},
            // Fix height so each child section carries its own ScrollView
            // instead of the parent container scrolling.
            {height: calendarPos.maxHeight},
          ]}>
          <Pressable
            style={[styles.contentPressable, {flex: 1}]}
            onPress={e => e.stopPropagation()}>
            {step === 'date' ? (
              // ── Step 1: Calendar ─────────────────────────────────────────────
              <View style={[styles.calendarCard, {flex: 1}]}>
                <View
                  style={styles.calendarHeader}
                  onLayout={e =>
                    setCalendarHeaderHeight(e.nativeEvent.layout.height)
                  }>
                  {/* <Text style={styles.calendarHeaderYear}>{selYear}</Text> */}
                  <Text style={styles.calendarHeaderDate}>{selLabel}</Text>
                </View>
                {/* Calendar gets its own scroll so it doesn't bubble up */}
                <ScrollView
                  style={{flex: 1}}
                  scrollEnabled={true}
                  showsVerticalScrollIndicator={true}>
                  <Calendar
                    key={currentMonth}
                    current={currentMonth}
                    onDayPress={handleDayPress}
                    onMonthChange={(m: {dateString: string}) => {
                      setCurrentMonth(m.dateString);
                    }}
                    theme={calendarTheme}
                    renderHeader={renderCalendarHeader}
                    renderArrow={(direction: 'left' | 'right') =>
                      direction === 'left' ? (
                        <CalendarArrowLeft
                          color={theme.calenderTextColor}
                          width={20}
                          height={20}
                        />
                      ) : (
                        <CalendarArrowRight
                          color={theme.calenderTextColor}
                          width={20}
                          height={20}
                        />
                      )
                    }
                    markingType="custom"
                    markedDates={
                      selected
                        ? {
                            [selected]: {
                              selected: true,
                              customStyles: {
                                container: {
                                  backgroundColor: palette.blue.paleSecondary,
                                  borderWidth:
                                    Typography.borderWidth.borderWidth1,
                                  borderColor: palette.ui.hoverBlue,
                                  borderRadius: Typography.radius.radius4,
                                  alignContent: 'center',
                                },
                                text: {
                                  color: palette.ui.hoverBlue,
                                  fontWeight: Typography.fontWeight.semiBold,
                                },
                              },
                              customContainerStyle: {
                                backgroundColor: palette.blue.paleSecondary,
                                borderWidth:
                                  Typography.borderWidth.borderWidth1,
                                borderColor: palette.ui.hoverBlue,
                                borderRadius: Typography.radius.radius4,
                                alignContent: 'center',
                              },
                              selectedColor: palette.blue.paleSecondary,
                              selectedTextColor: palette.ui.hoverBlue,
                            },
                          }
                        : {}
                    }
                  />
                </ScrollView>
                {/* ── Month picker overlay ─────────────────────────────────── */}
                {pickerMode === 'month' && (
                  <View
                    style={{
                      position: 'absolute',
                      top: calendarHeaderHeight,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor:
                        theme.workOrderStatausCalenderBG ?? '#fff',
                      zIndex: 10,
                    }}>
                    <ScrollView showsVerticalScrollIndicator>
                      <View
                        style={[styles.scrollViews, {padding: Typography.padding.padding16}]}>
                        {MONTH_NAMES.map((name, idx) => {
                          const isSelected = idx === displayedMonth;
                          return (
                            <TouchableOpacity
                              key={name}
                              onPress={() => handleMonthSelect(idx)}
                              style={{
                                width: '30%',
                                paddingVertical: Typography.padding.padding10,
                                alignItems: 'center',
                                borderRadius: Typography.radius.radius8,
                                backgroundColor: isSelected
                                  ? palette.blue.paleSecondary
                                  : 'transparent',
                                borderWidth: isSelected
                                  ? Typography.borderWidth.borderWidth1
                                  : 0,
                                borderColor: isSelected
                                  ? palette.ui.hoverBlue
                                  : 'transparent',
                              }}>
                              <Text
                                style={{
                                  color: isSelected
                                    ? palette.ui.hoverBlue
                                    : theme.calenderTextColor ?? '#333',
                                  fontSize: Typography.fontSize.fontSize13,
                                  fontWeight: isSelected
                                    ? Typography.fontWeight.semiBold
                                    : Typography.fontWeight.regular,
                                  fontFamily: Typography.fontFamily.inter,
                                }}>
                                {name.slice(0, 3)}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </ScrollView>
                  </View>
                )}
                {/* ── Year picker overlay ──────────────────────────────────── */}
                {pickerMode === 'year' && (
                  <View
                    style={{
                      position: 'absolute',
                      top: calendarHeaderHeight,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor:
                        theme.workOrderStatausCalenderBG ?? '#fff',
                      zIndex: 10,
                    }}>
                    <ScrollView showsVerticalScrollIndicator>
                      <View
                        style={styles.scrollViews}>
                        {yearList.map(yr => {
                          const isSelected = yr === displayedYear;
                          return (
                            <TouchableOpacity
                              key={yr}
                              onPress={() => handleYearSelect(yr)}
                              style={{
                                width: '22%',
                                paddingVertical: Typography.padding.padding10,
                                alignItems: 'center',
                                borderRadius: Typography.radius.radius8,
                                backgroundColor: isSelected
                                  ? palette.blue.paleSecondary
                                  : 'transparent',
                                borderWidth: isSelected
                                  ? Typography.borderWidth.borderWidth1
                                  : 0,
                                borderColor: isSelected
                                  ? palette.ui.hoverBlue
                                  : 'transparent',
                              }}>
                              <Text
                                style={{
                                  color: isSelected
                                    ? palette.ui.hoverBlue
                                    : theme.calenderTextColor ?? '#333',
                                  fontSize: Typography.fontSize.fontSize13,
                                  fontWeight: isSelected
                                    ? Typography.fontWeight.semiBold
                                    : Typography.fontWeight.regular,
                                  fontFamily: Typography.fontFamily.inter,
                                }}>
                                {yr}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </ScrollView>
                  </View>
                )}
              </View>
            ) : (
              // ── Step 2: Analog clock ──────────────────────────────────────────
              <ScrollView
                style={{flex: 1}}
                showsVerticalScrollIndicator={false}>
                <View style={styles.timeCard}>
                  {/* Header: HH:MM + AM/PM */}
                  <View style={styles.timeHeader}>
                    <View style={styles.timeDisplayRow}>
                      <TextInput
                        style={[
                          styles.timeDigit,
                          styles.timeInput,
                          isHourMode && styles.timeDigitActive,
                        ]}
                        value={hourText}
                        keyboardType="number-pad"
                        maxLength={2}
                        selectTextOnFocus
                        onFocus={() => {
                          editingHour.current = true;
                          setIsHourMode(true);
                        }}
                        onChangeText={v => {
                          if (v === '') {
                            setHourText('');
                            return;
                          }
                          const n = parseInt(v, 10);
                          if (isNaN(n) || n > 12) return;
                          setHourText(v);
                          if (n >= 1) setHour(n);
                        }}
                        onEndEditing={commitHour}
                        onBlur={commitHour}
                      />
                      <Text style={styles.timeColon}>{':'}</Text>
                      <TextInput
                        style={[
                          styles.timeDigit,
                          styles.timeInput,
                          !isHourMode && styles.timeDigitActive,
                        ]}
                        value={minuteText}
                        keyboardType="number-pad"
                        maxLength={2}
                        selectTextOnFocus
                        onFocus={() => {
                          editingMinute.current = true;
                          setIsHourMode(false);
                        }}
                        onChangeText={v => {
                          if (v === '') {
                            setMinuteText('');
                            return;
                          }
                          const n = parseInt(v, 10);
                          if (isNaN(n) || n > 59) return;
                          setMinuteText(v);
                          setMinute(n);
                        }}
                        onEndEditing={commitMinute}
                        onBlur={commitMinute}
                      />
                      <View style={styles.ampmColumn}>
                        <TouchableOpacity
                          onPress={() => {
                            setAmpm('AM');
                            emitAndClose(undefined, 'AM');
                          }}>
                          <Text
                            style={[
                              styles.ampmText,
                              ampm === 'AM' && styles.ampmActive,
                            ]}>
                            {'AM'}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => {
                            setAmpm('PM');
                            emitAndClose(undefined, 'PM');
                          }}>
                          <Text
                            style={[
                              styles.ampmText,
                              ampm === 'PM' && styles.ampmActive,
                            ]}>
                            {'PM'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                    {/* <Text style={styles.clockModeLabel}>
                    {isHourMode ? 'Select hour' : 'Select minute'}
                  </Text> */}
                  </View>

                  {/* Analog clock */}
                  <View style={styles.clockWrapper}>
                    <AnalogClock
                      selectedHour={hour}
                      selectedMinute={minute}
                      isHourMode={isHourMode}
                      onHourSelect={handleHourSelect}
                      onMinuteSelect={handleMinuteSelect}
                      faceColor={theme.workOrderStatausCalenderBG ?? '#EEEEEE'}
                      handColor={Colors.primaryLight}
                      numColor={theme.calenderTextColor ?? '#333'}
                      containerStyle={styles.clockContainer}
                    />
                  </View>
                </View>
              </ScrollView>
            )}
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
};

export default DatePicker;
